"use client";

import { useMemo } from "react";
import { useRouter } from "next/navigation";
import type { FunctionReference } from "convex/server";
import type { ReactMutation } from "convex/react";
import { useMutation } from "convex/react";

const unauthorizedPattern =
  /(unauthorized|not authorized|not authenticated|not signed in)/i;

export function isUnauthorizedError(error: unknown) {
  if (!error) return false;
  if (error instanceof Error) {
    return unauthorizedPattern.test(error.message);
  }
  if (typeof error === "string") {
    return unauthorizedPattern.test(error);
  }
  if (typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" && unauthorizedPattern.test(message);
  }
  return false;
}

function getNextPath() {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname}${window.location.search}`;
}

function wrapMutation<Mutation extends FunctionReference<"mutation">>(
  mutationFn: ReactMutation<Mutation>,
  router: ReturnType<typeof useRouter>
): ReactMutation<Mutation> {
  const wrapped = (async (...args: Parameters<typeof mutationFn>) => {
    try {
      return await mutationFn(...args);
    } catch (error) {
      if (isUnauthorizedError(error)) {
        const next = getNextPath();
        router.replace(`/login?next=${encodeURIComponent(next)}`);
        return undefined as unknown as Awaited<ReturnType<typeof mutationFn>>;
      }
      throw error;
    }
  }) as ReactMutation<Mutation>;

  wrapped.withOptimisticUpdate = (optimisticUpdate) =>
    wrapMutation(mutationFn.withOptimisticUpdate(optimisticUpdate), router);

  return wrapped;
}

export function useMutationWithAuthRedirect<
  Mutation extends FunctionReference<"mutation">
>(mutation: Mutation): ReactMutation<Mutation> {
  const router = useRouter();
  const mutationFn = useMutation(mutation);

  return useMemo(
    () => wrapMutation(mutationFn, router),
    [mutationFn, router]
  ) as ReactMutation<Mutation>;
}
